import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { Brain, Send, ArrowLeft, Clock, Database, Plus, MessageSquare, Loader2, Mail, Phone, ChevronDown, ChevronUp, Volume2, VolumeX, RotateCcw, FolderKanban, Building, User, Mic, ThumbsUp, ThumbsDown, FileText, Bug, Filter, Zap, Calendar, UserCheck, X } from 'lucide-react';
import { ScopeCombobox } from '../components/scope/ScopeCombobox';
import { AsyncScopeCombobox } from '../components/scope/AsyncScopeCombobox';
import { LanguageCombobox } from '../components/voice/LanguageCombobox';
import { toast } from 'sonner';
import axios from 'axios';
import { ChatLexicon } from '../components/ChatLexicon';

import ReactMarkdown from 'react-markdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SourcesPanel({ sources, sourceBreakdown, memoriesUsed, typeColors, typeLabels }) {
  const [open, setOpen] = useState(false);
  const hasSources = sources && sources.length > 0;

  return (
    <div className="mt-1.5 ml-1" data-testid="sources-panel">
      {hasSources ? (
        <>
          {/* Source badges */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <Database className="w-3 h-3 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{memoriesUsed} source{memoriesUsed > 1 ? 's' : ''}</span>
            <span className="text-muted-foreground/40 text-xs">|</span>
            {Object.entries(sourceBreakdown || {}).map(([type, count]) => (
              <span
                key={type}
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium ${typeColors[type] || 'bg-muted text-muted-foreground'}`}
              >
                {typeLabels[type] || type}: {count}
              </span>
            ))}
            <button
              onClick={() => setOpen(!open)}
              className="inline-flex items-center gap-0.5 text-xs text-primary hover:text-primary/80 transition-colors ml-1"
              data-testid="toggle-sources-btn"
            >
              {open ? 'Masquer' : 'Voir les sources'}
              {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          </div>
          {/* Sources accordion */}
          {open && (
            <div className="mt-2 space-y-1.5 border-l-2 border-primary/20 pl-3" data-testid="sources-list">
              {sources.map((src, i) => (
                <div key={i} className="text-xs">
                  <div className="flex items-center gap-1.5">
                    <span className={`inline-flex px-1.5 py-0.5 rounded font-medium ${typeColors[src.type] || 'bg-muted text-muted-foreground'}`}>
                      {(typeLabels[src.type] || src.type).replace(/s$/, '')}
                    </span>
                    <span className="text-muted-foreground">{src.date}</span>
                    {src.contact && <span className="text-foreground font-medium">{src.contact}</span>}
                  </div>
                  <p className="text-muted-foreground mt-0.5 line-clamp-1">{src.summary}</p>
                </div>
              ))}
            </div>
          )}
        </>
      ) : memoriesUsed === 0 ? (
        <p className="text-xs text-muted-foreground/70 italic">
          Aucune source precise trouvee, reponse basee sur le contexte disponible.
        </p>
      ) : null}
    </div>
  );
}

// ── Debug Panel (Niveau 1) — Owner/Admin only ──
const TIME_FILTER_LABELS = {
  today: "Aujourd'hui", today_morning: "Ce matin", today_afternoon: "Cet apres-midi",
  yesterday: "Hier", day_before_yesterday: "Avant-hier",
  this_week: "Cette semaine", last_week: "Semaine derniere",
  this_month: "Ce mois", last_month: "Mois dernier",
  named_month: "Mois specifique", since_month: "Depuis",
  last_n_days: "Derniers jours", ago_n_weeks: "Il y a N semaines",
  ago_n_months: "Il y a N mois", last_named_day: "Jour passe",
  recent: "Recemment",
};
const MONTH_NAMES = ["", "Janvier", "Fevrier", "Mars", "Avril", "Mai", "Juin", "Juillet", "Aout", "Septembre", "Octobre", "Novembre", "Decembre"];
const DAY_NAMES_FR = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

function formatTimeFilter(tf) {
  if (!tf) return "Aucun";
  const kind = tf.kind;
  const label = TIME_FILTER_LABELS[kind] || kind;
  if (kind === "named_month" || kind === "since_month") return `${label}: ${MONTH_NAMES[tf.value] || tf.value}`;
  if (kind === "last_n_days") return `${tf.value} derniers jours`;
  if (kind === "ago_n_weeks") return `Il y a ${tf.value} semaine(s)`;
  if (kind === "ago_n_months") return `Il y a ${tf.value} mois`;
  if (kind === "last_named_day") return `${DAY_NAMES_FR[tf.value] || tf.value} dernier`;
  return label;
}

function DebugPanel({ retrieval, responseTimeMs, memoriesUsed }) {
  const [open, setOpen] = useState(false);
  const rf = retrieval?.resolved_filters;
  if (!retrieval) return null;

  return (
    <div className="mt-1" data-testid="debug-panel">
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 text-[10px] text-muted-foreground/50 hover:text-muted-foreground transition-colors"
        data-testid="debug-toggle-btn"
      >
        <Bug className="w-3 h-3" />
        {open ? 'Masquer debug' : 'Debug RAG'}
      </button>
      {open && (
        <div className="mt-1.5 p-2.5 rounded-md bg-muted/40 border border-border/50 text-[11px] space-y-1.5" data-testid="debug-panel-content">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3 h-3 text-blue-500" />
              <span className="text-muted-foreground">Periode:</span>
              <span className="font-medium">{formatTimeFilter(rf?.time_filter)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <UserCheck className="w-3 h-3 text-emerald-500" />
              <span className="text-muted-foreground">Contact:</span>
              <span className="font-medium">{rf?.contact_name || "Aucun"}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Filter className="w-3 h-3 text-purple-500" />
              <span className="text-muted-foreground">Type:</span>
              <span className="font-medium">{rf?.source_type || "Tous"}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Zap className="w-3 h-3 text-amber-500" />
              <span className="text-muted-foreground">Mode:</span>
              <span className="font-medium">{retrieval.retrieval_mode || retrieval.mode || "—"}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Database className="w-3 h-3 text-muted-foreground" />
              <span className="text-muted-foreground">Memoires:</span>
              <span className="font-medium">{memoriesUsed ?? "—"}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="w-3 h-3 text-muted-foreground" />
              <span className="text-muted-foreground">Temps:</span>
              <span className="font-medium">{responseTimeMs ? `${responseTimeMs}ms` : "—"}</span>
            </div>
          </div>
          {(retrieval.inherited_filters?.length > 0 || retrieval.overridden_filters?.length > 0) && (
            <div className="flex gap-3 pt-1 border-t border-border/30">
              {retrieval.inherited_filters?.length > 0 && (
                <span className="text-muted-foreground/70">Herite: {retrieval.inherited_filters.join(", ")}</span>
              )}
              {retrieval.overridden_filters?.length > 0 && (
                <span className="text-muted-foreground/70">Remplace: {retrieval.overridden_filters.join(", ")}</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Feedback Reasons (Niveau 2) ──
const FEEDBACK_REASONS = [
  { value: "wrong_period", label: "Mauvaise periode", icon: Calendar },
  { value: "wrong_contact", label: "Mauvais contact", icon: UserCheck },
  { value: "irrelevant_memories", label: "Sources non pertinentes", icon: Database },
  { value: "incomplete", label: "Reponse incomplete", icon: FileText },
  { value: "off_topic", label: "Hors sujet / hallucination", icon: Brain },
  { value: "other", label: "Autre", icon: MessageSquare },
];

export default function CloneChatPage() {
  const { personId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const reportTriggeredRef = useRef(false);
  
  const [person, setPerson] = useState(null);
  const [messages, setMessages] = useState([]);
  const [memories, setMemories] = useState([]);
  const [memoryStats, setMemoryStats] = useState(null);
  const [feedbackStats, setFeedbackStats] = useState({ up: 0, down: 0 });
  const [feedbackPopoverIdx, setFeedbackPopoverIdx] = useState(null);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [speakingId, setSpeakingId] = useState(null);
  const [activeTab, setActiveTab] = useState('chat');
  const audioRef = useRef(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const sessionIdRef = useRef(null);

  // Scope state for filtered chat
  const [scope, setScope] = useState(null);
  const [scopeId, setScopeId] = useState(null);
  const [chatLang, setChatLang] = useState('auto');

  useEffect(() => {
    fetchPerson();
    fetchMemories();
    fetchMemoryStats();
    fetchFeedbackStats();
    initSession();
  }, [personId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-trigger weekly report if ?report=weekly in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('report') === 'weekly' && !loading && person && !reportTriggeredRef.current) {
      reportTriggeredRef.current = true;
      // Clean up URL
      navigate(`/clones/${personId}/chat`, { replace: true });
      handleWeeklyReport();
    }
  }, [loading, person]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const initSession = async () => {
    try {
      const res = await axios.get(`${API}/persons/${personId}/chat/latest-session`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const latestSession = res.data.session_id;
      if (latestSession) {
        sessionIdRef.current = latestSession;
        fetchChatHistory(latestSession);
      } else {
        sessionIdRef.current = crypto.randomUUID();
        setMessages([]);
      }
    } catch {
      sessionIdRef.current = crypto.randomUUID();
      setMessages([]);
    }
  };

  const handleNewConversation = () => {
    // Stop any playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setSpeakingId(null);
    }
    sessionIdRef.current = crypto.randomUUID();
    setMessages([]);
    inputRef.current?.focus();
    toast.success('Nouvelle conversation démarrée');
  };

  const fetchPerson = async () => {
    try {
      const response = await axios.get(`${API}/persons/${personId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPerson(response.data);
    } catch (error) {
      toast.error('Clone not found');
      navigate('/clones');
    } finally {
      setLoading(false);
    }
  };

  const fetchMemories = async () => {
    try {
      const response = await axios.get(`${API}/persons/${personId}/memories`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 50 }
      });
      setMemories(response.data);
    } catch (error) {
      console.error('Failed to fetch memories:', error);
    }
  };

  const fetchMemoryStats = async () => {
    try {
      const response = await axios.get(`${API}/persons/${personId}/memories/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMemoryStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchFeedbackStats = async () => {
    try {
      const response = await axios.get(`${API}/persons/${personId}/chat/feedback-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedbackStats(response.data);
    } catch {}
  };

  const handleScopeChange = (newScope, newScopeId) => {
    setScope(newScope);
    setScopeId(newScopeId);
    handleNewConversation();
  };

  const fetchChatHistory = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/persons/${personId}/chat/history`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 50, session_id: sessionId }
      });
      // Convert history to messages format
      const historyMessages = [];
      response.data.reverse().forEach(log => {
        historyMessages.push({
          role: 'user',
          content: log.message,
          timestamp: log.created_at
        });
        historyMessages.push({
          role: 'assistant',
          content: log.response,
          memories_used: log.memory_count,
          chat_log_id: log.id,
          feedback: log.feedback || null,
          feedback_reason: log.feedback_reason || null,
          response_time_ms: log.response_time_ms,
          retrieval: log.resolved_filters ? {
            resolved_filters: log.resolved_filters,
            retrieval_mode: log.retrieval_mode,
            mode: log.retrieval_mode,
            memories_used: log.memory_count,
            inherited_filters: log.inherited_filters || [],
            overridden_filters: log.overridden_filters || [],
          } : null,
          timestamp: log.created_at
        });
      });
      setMessages(historyMessages);
    } catch (error) {
      console.error('Failed to fetch chat history:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || sending) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setSending(true);

    // Add user message immediately
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);

    try {
      const payload = {
        message: userMessage,
        max_memories: 10,
        session_id: sessionIdRef.current,
        language: chatLang === 'auto' ? null : chatLang,
      };
      if (scope && scopeId) {
        payload.scope = scope;
        payload.scope_id = scopeId;
      }

      const response = await axios.post(
        `${API}/persons/${personId}/chat`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Add assistant response
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response,
        memories_used: response.data.memories_used,
        sources: response.data.sources || [],
        source_breakdown: response.data.source_breakdown || {},
        retrieval: response.data.retrieval || {},
        response_time_ms: response.data.response_time_ms,
        chat_log_id: response.data.chat_log_id,
        feedback: null,
        feedback_reason: null,
        timestamp: new Date().toISOString()
      }]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
      // Remove the user message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleWeeklyReport = async () => {
    if (generatingReport || sending) return;
    setGeneratingReport(true);

    setMessages(prev => [...prev, {
      role: 'user',
      content: 'Rapport hebdomadaire',
      timestamp: new Date().toISOString(),
      isReport: true,
    }]);

    try {
      const response = await axios.post(
        `${API}/persons/${personId}/report/weekly`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 180000 }
      );

      const data = response.data;
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.report,
        memories_used: data.total_memories,
        source_breakdown: data.source_breakdown || {},
        sources: [],
        retrieval: {
          mode: 'weekly_report',
          total_memories: data.total_memories,
          total_contacts: data.total_contacts,
          batches: data.batches,
        },
        timestamp: new Date().toISOString(),
        isReport: true,
      }]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la generation du rapport');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setGeneratingReport(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const handleSpeak = async (text, msgIndex) => {
    // Stop current audio if playing same message
    if (speakingId === msgIndex && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setSpeakingId(null);
      return;
    }
    
    setSpeakingId(msgIndex);
    try {
      const response = await axios.post(
        `${API}/persons/${personId}/speak`,
        { text: text.slice(0, 4000) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const audioBytes = Uint8Array.from(atob(response.data.audio), c => c.charCodeAt(0));
      const blob = new Blob([audioBytes], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        setSpeakingId(null);
        URL.revokeObjectURL(url);
      };
      audio.play();
    } catch (error) {
      toast.error('Erreur de synthese vocale');
      setSpeakingId(null);
    }
  };

  const handleFeedback = async (msgIndex, value, reason = null) => {
    const msg = messages[msgIndex];
    if (!msg?.chat_log_id) return;

    // Toggle: if same feedback without reason change, remove it
    const newFeedback = (msg.feedback === value && !reason) ? null : value;

    try {
      await axios.post(
        `${API}/persons/${personId}/chat/${msg.chat_log_id}/feedback`,
        { feedback: newFeedback, feedback_reason: reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev => prev.map((m, i) => i === msgIndex ? { ...m, feedback: newFeedback, feedback_reason: reason } : m));
      setFeedbackPopoverIdx(null);
      fetchFeedbackStats();
    } catch {
      toast.error('Erreur lors de l\'envoi du feedback');
    }
  };

  const handleThumbsDown = (msgIndex) => {
    const msg = messages[msgIndex];
    if (msg.feedback === 'down') {
      // Already down — toggle off
      handleFeedback(msgIndex, null);
    } else {
      // Show reason popover
      setFeedbackPopoverIdx(msgIndex);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const typeColors = {
    call: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    email: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
    sms: 'bg-green-500/10 text-green-600 dark:text-green-400',
    note: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    meeting: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
    whatsapp: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
  };

  const typeLabels = {
    call: 'Appels',
    email: 'Emails',
    sms: 'SMS',
    whatsapp: 'WhatsApp',
    note: 'Notes',
    meeting: 'Réunions'
  };

  if (loading) {
    return (
      <DashboardLayout title="Chat">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  const initials = person?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  const isSharedClone = person && person.linked_user_id !== user?.id && user?.role !== 'owner' && user?.role !== 'admin';

  return (
    <DashboardLayout title={`Chat with ${person?.name || 'Clone'}`}>
      <div className="h-[calc(100vh-8rem)] flex flex-col">
        {/* Shared clone banner */}
        {isSharedClone && (
          <div className="flex items-center gap-2 px-4 py-2 mb-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-sm text-blue-400" data-testid="shared-clone-banner">
            <User className="w-4 h-4 shrink-0" />
            <span>Vous parlez au clone de <strong>{person?.name}</strong></span>
          </div>
        )}
        {/* Header */}
        <div className="flex items-center gap-4 mb-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/clones')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Avatar className="h-12 w-12">
            <AvatarImage src={person?.avatar_url} />
            <AvatarFallback className="bg-primary/10 text-primary text-lg">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <h2 className="text-xl font-heading font-bold">{person?.name}</h2>
            <p className="text-sm text-muted-foreground">{person?.job_title} • {person?.department}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleWeeklyReport}
              disabled={generatingReport || sending}
              className="gap-1.5"
              data-testid="weekly-report-btn"
            >
              {generatingReport ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Rapport hebdo
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNewConversation}
              className="gap-1.5"
              data-testid="new-conversation-btn"
            >
              <RotateCcw className="w-4 h-4" />
              Nouvelle conversation
            </Button>
            <Badge variant="secondary" className="gap-1">
              <Database className="w-3 h-3" />
              {memoryStats?.total_memories || 0} memories
            </Badge>
            {(feedbackStats.up > 0 || feedbackStats.down > 0) && (
              <Badge variant="outline" className="gap-1.5 text-xs" data-testid="feedback-stats-badge">
                <ThumbsUp className="w-3 h-3 text-emerald-500" /> {feedbackStats.up}
                <ThumbsDown className="w-3 h-3 text-red-500 ml-1" /> {feedbackStats.down}
              </Badge>
            )}
          </div>
        </div>

        {/* Scope Selector */}
        <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="scope-selector">
          <span className="text-xs text-muted-foreground mr-1">Scope :</span>
          <Button
            variant={!scope ? 'default' : 'outline'}
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={() => handleScopeChange(null, null)}
            data-testid="scope-clone"
          >
            <Brain className="w-3 h-3" /> Clone
          </Button>
          <AsyncScopeCombobox
            token={token}
            endpoint="/dossiers"
            extractItems={(data) => Array.isArray(data) ? data : data.dossiers || []}
            value={scope === 'dossier' ? scopeId : null}
            onSelect={(id) => id ? handleScopeChange('dossier', id) : handleScopeChange(null, null)}
            placeholder="Dossier..."
            emptyText="Aucun dossier."
            labelFn={(d) => d.title || d.name || d.id}
            icon={FolderKanban}
            limitParam="limit"
            limitValue={10}
            testId="scope-dossier-select"
          />
          <AsyncScopeCombobox
            token={token}
            endpoint="/organizations"
            extractItems={(data) => Array.isArray(data) ? data : data.organizations || []}
            value={scope === 'organization' ? scopeId : null}
            onSelect={(id) => id ? handleScopeChange('organization', id) : handleScopeChange(null, null)}
            placeholder="Organisation..."
            emptyText="Aucune organisation."
            labelFn={(o) => o.name}
            icon={Building}
            testId="scope-org-select"
          />
          <AsyncScopeCombobox
            token={token}
            endpoint="/contacts"
            extractItems={(data) => data.contacts || (Array.isArray(data) ? data : [])}
            value={scope === 'contact' ? scopeId : null}
            onSelect={(id) => id ? handleScopeChange('contact', id) : handleScopeChange(null, null)}
            placeholder="Contact..."
            emptyText="Aucun contact."
            labelFn={(c) => `${c.first_name || ''} ${c.last_name || ''}`.trim()}
            icon={User}
            testId="scope-contact-select"
          />
          {scope && (
            <Badge variant="outline" className="text-xs h-7 gap-1 border-primary/30 text-primary">
              {scope === 'dossier' && <FolderKanban className="w-3 h-3" />}
              {scope === 'organization' && <Building className="w-3 h-3" />}
              {scope === 'contact' && <User className="w-3 h-3" />}
              Filtre actif
            </Badge>
          )}
          <div className="ml-auto">
            <LanguageCombobox
              value={chatLang}
              onSelect={setChatLang}
              testId="chat-language-select"
            />
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <TabsList className="w-fit">
            <TabsTrigger value="chat" className="gap-2" data-testid="tab-chat">
              <MessageSquare className="w-4 h-4" />
              Chat
            </TabsTrigger>
            <TabsTrigger value="memories" className="gap-2" data-testid="tab-memories">
              <Brain className="w-4 h-4" />
              Memories ({memoryStats?.total_memories || memories.length})
            </TabsTrigger>
          </TabsList>

          {/* Chat Tab */}
          <TabsContent value="chat" className="flex-1 flex flex-col mt-4">
            <Card className="flex-1 flex flex-col" data-testid="chat-card">
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {messages.length === 0 ? (
                    <div className="py-8">
                      <ChatLexicon
                        type="clone"
                        personName={person?.name}
                        onSelect={(q) => { setInputMessage(q); }}
                      />
                    </div>
                  ) : (
                    messages.map((msg, index) => (
                      <div
                        key={index}
                        className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        data-testid={`message-${index}`}
                      >
                        {msg.role === 'assistant' && (
                          <Avatar className="h-8 w-8 flex-shrink-0">
                            <AvatarFallback className="bg-primary/10 text-primary text-xs">
                              {initials}
                            </AvatarFallback>
                          </Avatar>
                        )}
                        <div className={`max-w-[80%] ${msg.role === 'user' ? '' : ''}`}>
                          <div
                            className={`rounded-2xl px-4 py-2.5 ${
                              msg.role === 'user'
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted'
                            }`}
                          >
                            {msg.role === 'assistant' ? (
                              <div className="text-sm prose prose-sm max-w-none dark:prose-invert prose-headings:text-base prose-headings:font-semibold prose-headings:mt-3 prose-headings:mb-1 prose-p:my-1 prose-ul:my-1 prose-li:my-0">
                                <ReactMarkdown>{msg.content}</ReactMarkdown>
                              </div>
                            ) : (
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            )}
                            <div className={`flex items-center gap-2 mt-1.5 text-xs ${
                              msg.role === 'user' ? 'text-primary-foreground/70' : 'text-muted-foreground'
                            }`}>
                              <span>{formatTime(msg.timestamp)}</span>
                              {msg.role === 'assistant' && (
                                <>
                                  <button
                                    onClick={() => handleSpeak(msg.content, index)}
                                    className="flex items-center gap-1 hover:text-primary transition-colors"
                                    data-testid={`speak-btn-${index}`}
                                    title={speakingId === index ? "Arreter" : "Ecouter"}
                                  >
                                    {speakingId === index ? (
                                      <><VolumeX className="w-3.5 h-3.5" /> Stop</>
                                    ) : (
                                      <><Volume2 className="w-3.5 h-3.5" /> Ecouter</>
                                    )}
                                  </button>
                                  <span className="text-muted-foreground/30">|</span>
                                  <button
                                    onClick={() => handleFeedback(index, 'up')}
                                    className={`flex items-center transition-colors ${msg.feedback === 'up' ? 'text-emerald-500' : 'hover:text-emerald-500'}`}
                                    data-testid={`feedback-up-${index}`}
                                    title="Bonne reponse"
                                  >
                                    <ThumbsUp className="w-3.5 h-3.5" />
                                  </button>
                                  <div className="relative">
                                    <button
                                      onClick={() => handleThumbsDown(index)}
                                      className={`flex items-center transition-colors ${msg.feedback === 'down' ? 'text-red-500' : 'hover:text-red-500'}`}
                                      data-testid={`feedback-down-${index}`}
                                      title="Mauvaise reponse"
                                    >
                                      <ThumbsDown className="w-3.5 h-3.5" />
                                    </button>
                                    {feedbackPopoverIdx === index && (
                                      <div className="absolute bottom-full left-0 mb-2 z-50 bg-popover border border-border rounded-lg shadow-lg p-2 w-56" data-testid={`feedback-reason-popover-${index}`}>
                                        <div className="flex items-center justify-between mb-1.5">
                                          <span className="text-[11px] font-medium text-muted-foreground">Pourquoi ?</span>
                                          <button onClick={() => setFeedbackPopoverIdx(null)} className="text-muted-foreground/50 hover:text-muted-foreground">
                                            <X className="w-3 h-3" />
                                          </button>
                                        </div>
                                        <div className="space-y-0.5">
                                          {FEEDBACK_REASONS.map(r => (
                                            <button
                                              key={r.value}
                                              onClick={() => handleFeedback(index, 'down', r.value)}
                                              className="flex items-center gap-2 w-full px-2 py-1.5 rounded text-xs hover:bg-muted transition-colors text-left"
                                              data-testid={`feedback-reason-${r.value}`}
                                            >
                                              <r.icon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                                              {r.label}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  {msg.feedback === 'down' && msg.feedback_reason && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-600">
                                      {FEEDBACK_REASONS.find(r => r.value === msg.feedback_reason)?.label || msg.feedback_reason}
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                          {/* Source badges + accordion for assistant messages */}
                          {msg.role === 'assistant' && (
                            <>
                              <SourcesPanel
                                sources={msg.sources}
                                sourceBreakdown={msg.source_breakdown}
                                memoriesUsed={msg.memories_used}
                                typeColors={typeColors}
                                typeLabels={typeLabels}
                              />
                              {/* Retrieval metadata */}
                              {msg.retrieval && (msg.retrieval.memories_used > 0 || msg.retrieval.mode === 'map_reduce' || msg.retrieval.mode === 'weekly_report') && (
                                <div className="flex flex-wrap gap-2 mt-1.5" data-testid={`retrieval-meta-${messages.indexOf(msg)}`}>
                                  {msg.retrieval.mode === 'map_reduce' && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-700 font-medium" data-testid="map-reduce-badge">
                                      Synthese complete ({msg.retrieval.total_memories} interactions, {msg.retrieval.batches} lots)
                                    </span>
                                  )}
                                  {msg.retrieval.mode === 'weekly_report' && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-700 font-medium" data-testid="weekly-report-badge">
                                      Rapport hebdomadaire ({msg.retrieval.total_memories} interactions, {msg.retrieval.total_contacts} contacts)
                                    </span>
                                  )}
                                  {msg.retrieval.rewritten_query && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 italic max-w-md truncate" title={msg.retrieval.rewritten_query} data-testid="rewritten-query-badge">
                                      Reformulation: {msg.retrieval.rewritten_query.substring(0, 80)}{msg.retrieval.rewritten_query.length > 80 ? '...' : ''}
                                    </span>
                                  )}
                                  {msg.retrieval.detected_client && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-700">
                                      Contact : {msg.retrieval.detected_client.name}
                                    </span>
                                  )}
                                  {msg.retrieval.sources_breakdown && Object.entries(msg.retrieval.sources_breakdown).map(([type, count]) => (
                                    <span key={type} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                      {type}: {count}
                                    </span>
                                  ))}
                                  {msg.retrieval.memories_used > 0 && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                      {msg.retrieval.memories_used} source(s)
                                    </span>
                                  )}
                                </div>
                              )}
                              {/* Debug Panel — Owner/Admin only */}
                              {(user?.role === 'owner' || user?.role === 'admin') && (
                                <DebugPanel
                                  retrieval={msg.retrieval}
                                  responseTimeMs={msg.response_time_ms}
                                  memoriesUsed={msg.memories_used}
                                />
                              )}
                            </>
                          )}
                        </div>
                        {msg.role === 'user' && (
                          <Avatar className="h-8 w-8 flex-shrink-0">
                            <AvatarFallback className="bg-emerald-500/10 text-emerald-600 text-xs">
                              {user?.first_name?.[0]}{user?.last_name?.[0]}
                            </AvatarFallback>
                          </Avatar>
                        )}
                      </div>
                    ))
                  )}
                  {sending && (
                    <div className="flex gap-3 justify-start">
                      <Avatar className="h-8 w-8 flex-shrink-0">
                        <AvatarFallback className="bg-primary/10 text-primary text-xs">
                          {initials}
                        </AvatarFallback>
                      </Avatar>
                      <div className="bg-muted rounded-2xl px-4 py-3">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>
              
              {/* Input */}
              <div className="p-4 border-t border-border">
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <Input
                    ref={inputRef}
                    placeholder={`Ask ${person?.name} something...`}
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    disabled={sending}
                    className="flex-1"
                    data-testid="chat-input"
                  />
                  <Button type="submit" disabled={!inputMessage.trim() || sending} data-testid="send-btn">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </form>
                {/* Voice hint */}
                <button
                  onClick={() => navigate(`/clones/${personId}/voice`)}
                  className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground hover:text-primary transition-colors"
                  data-testid="voice-hint"
                >
                  <Mic className="w-3 h-3" />
                  Essayez aussi en vocal avec la Voix IA
                </button>
              </div>
            </Card>
          </TabsContent>

          {/* Memories Tab */}
          <TabsContent value="memories" className="flex-1 mt-4">
            <Card className="h-full" data-testid="memories-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Memory Bank</CardTitle>
                    <CardDescription>
                      {memoryStats?.total_memories || 0} memories indexed
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate(`/clones/${personId}/memories/new`)} data-testid="add-memory-btn">
                    <Plus className="w-4 h-4" />
                    Add Memory
                  </Button>
                  <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate(`/clones/${personId}/email`)} data-testid="connect-email-btn">
                    <Mail className="w-4 h-4" />
                    Email
                  </Button>
                  <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate(`/clones/${personId}/ringover`)} data-testid="connect-ringover-btn">
                    <Phone className="w-4 h-4" />
                    Calls
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[calc(100vh-20rem)]">
                  <div className="space-y-3">
                    {memories.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-medium">No memories yet</p>
                        <p className="text-sm mt-1">Add memories to enable RAG chat</p>
                      </div>
                    ) : (
                      memories.map((memory) => (
                        <div
                          key={memory.id}
                          className="p-4 rounded-lg border border-border hover:border-primary/50 transition-colors"
                          data-testid={`memory-${memory.id}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="secondary" className={typeColors[memory.type]}>
                                  {memory.type}
                                </Badge>
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formatDate(memory.timestamp)}
                                </span>
                              </div>
                              <h4 className="font-medium text-sm">{memory.summary}</h4>
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                {memory.memory_text}
                              </p>
                              {memory.contact_name && (
                                <p className="text-xs text-muted-foreground mt-2">
                                  Contact: {memory.contact_name}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
